import React, { useState } from 'react';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import { IImage } from '@/components/admin/Images/interfaces';
import { TargetImageCard } from './TargetImageCard';
import { useImages, useMergedImageModels, useMergedVersions } from './MergeHistoryModal.utils';
import { MergedImageCard } from './MergedImageCard';
import { SelectAllButton } from '../SelectAllButton';

interface ModalContentProps {
  image: IImage;
}

export const ModalContent = ({ image: targetImage }: ModalContentProps) => {
  const [revertedImageIds, setRevertedImageIds] = useState<number[]>([]);
  const { data: images } = useImages({ image: targetImage });
  const { data: versions } = useMergedVersions({ image: targetImage });
  const { data: imageModels } = useMergedImageModels({ image: targetImage });

  const addRevertedImage = (image: Partial<{ id: number }>) => {
    setRevertedImageIds([...new Set([...revertedImageIds, image.id])]);
  };

  return (
    <div>
      <Text className="tw-mb-3" size="sm" variant="body">
        Below are images merged into the target.
        Reverting a merge will restore previous uses to the corresponding image.
      </Text>
      <Text className="tw-mb-3" size="sm" variant="label" weight="bold">
        Target Image:
      </Text>
      <TargetImageCard image={targetImage} />

      <Text className={cn({ 'tw-mb-3': images.length > 0 }, 'tw-mt-3')} size="sm" variant="body" weight="bold">
        {`Images merged into this target (${images.length}):`}
      </Text>
      <SelectAllButton image={targetImage} revertedImageIds={revertedImageIds} />
      {images.length > 0 && (
        <div className="tw-border tw-rounded tw-border-solid tw-border-med-grey tw-max-h-72 tw-overflow-y-auto">
          {images.map((image) => {
            const version = versions.find(v => v.meta?.merged_from.toString() === image.id.toString());
            const imageModel = imageModels.find(im => im.id.toString() === version?.item_id?.toString());

            return (
              <MergedImageCard
                key={image.id}
                addToReverted={() => addRevertedImage(image)}
                image={image}
                previousImageModel={imageModel}
                targetImage={targetImage}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
